import { InlineLoading } from "@carbon/react";
import { DrawerTransition, useWindowEvent } from "@flaminem/ui-components";
import { debounce } from "@flaminem/utils";
import Cytoscape, { ElementDefinition, EventObject, LayoutOptions, Stylesheet } from "cytoscape";
import cola from "cytoscape-cola";
import elk from "cytoscape-elk";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTheme } from "src/theme";

import { GRAPH_SIDE_PANEL_WIDTH_REM } from "./graph.constants";
import { GraphNode } from "./graph.types";
import { GraphNodePassiveModal } from "./GraphNodePassiveModal";

Cytoscape.use(elk);
Cytoscape.use(cola);

type RelatedCasesGraphComponentProps = {
  graphData: ElementDefinition[];
  graphLayout: LayoutOptions;
  graphStyles: Stylesheet[];
  isLoading: boolean;
  startButton?: React.RefObject<HTMLButtonElement | null>;
  fitButton?: React.RefObject<HTMLButtonElement | null>;
  isFullScreen: boolean;
};

/**
 * Get the overlap width between the graph container and the side panel
 * It assumes that the side panel is on the right side of the graph container
 * and that the side panel doesn't cover all graph container
 * and that the side panel is fixed width stored in `GRAPH_SIDE_PANEL_WIDTH_REM`
 * @param graphContainer - The container element of the graph
 */
const getOverlapWidth = (graphContainer: HTMLElement) => {
  const boxBWidth = GRAPH_SIDE_PANEL_WIDTH_REM * 16;
  const viewportWidth = window.innerWidth;
  const rect = graphContainer.getBoundingClientRect();
  /** Padding between right side of the viewport and the right side of the graph container */
  const paddingRight = viewportWidth - rect.right;
  /** Overlap between the width of the graph container and the width of the side panel */
  const overlapWidth = boxBWidth - paddingRight;
  return overlapWidth;
};

const RelatedCasesGraphComponent: React.FC<RelatedCasesGraphComponentProps> = ({
  graphData,
  graphLayout,
  graphStyles,
  isLoading,
  startButton,
  fitButton,
  isFullScreen,
}) => {
  const { theme } = useTheme();
  const cyRef = useRef<cytoscape.Core>(undefined);
  const [rightShift, setRightShift] = useState(0);
  const graphRef = useRef<HTMLDivElement>(null);
  const graphContainerRef = useRef<HTMLDivElement>(null);
  const [selectedNode, setSelectedNode] = useState<GraphNode | undefined>(undefined);

  const resizeHandler = useMemo(
    () =>
      debounce(() => {
        if (!selectedNode) return;
        const overlap = getOverlapWidth(graphContainerRef.current!);
        setRightShift(overlap ?? 0);
      }, 300),
    [selectedNode]
  );

  useWindowEvent("resize", resizeHandler);

  const onNodeClick = useCallback((event: EventObject) => {
    if (!event.target?.isNode()) return;
    const nodeType = event.target?.data()?.type;
    if (nodeType && nodeType !== "identifier") {
      const overlap = getOverlapWidth(graphContainerRef.current!);
      setRightShift(overlap ?? 0);
      setSelectedNode(event.target as GraphNode);
    }
  }, []);

  const resetCytoscape = useCallback(() => {
    if (graphRef.current) {
      cyRef.current = Cytoscape({
        container: graphRef.current,
        elements: graphData,
        style: graphStyles,
        headless: false,
        minZoom: 0.1,
      });
      const layout = cyRef.current?.layout(graphLayout);
      cyRef.current?.on("layoutstop resize", () => {
        cyRef.current?.fit();
      });

      cyRef.current?.on("click", onNodeClick);

      if (startButton?.current) {
        startButton.current.addEventListener("click", function () {
          layout?.start();
          startButton.current!.style.visibility = "hidden";
          layout?.on("layoutstop", () => {
            startButton.current!.style.visibility = "visible";
          });
        });
      }

      if (fitButton?.current) {
        fitButton.current.addEventListener("click", () => {
          cyRef.current?.fit();
        });
      }

      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          setSelectedNode(undefined);
          cyRef.current?.fit();
        }
      });

      layout?.run();
    }
  }, [graphData, graphStyles, graphLayout, startButton, onNodeClick, fitButton]);

  useEffect(() => {
    resetCytoscape();
  }, [resetCytoscape]);

  useEffect(() => {
    if (cyRef.current) {
      cyRef.current.style(graphStyles);
    }
  }, [graphStyles, theme]);

  const resetSelectedNode = () => {
    cyRef.current?.fit();
    setSelectedNode(undefined);
  };

  useEffect(() => {
    // When the user exits full screen, we reset the selected node
    if (!isFullScreen) {
      setSelectedNode(undefined);
    }
  }, [isFullScreen]);

  const width = selectedNode && rightShift ? `calc(100% - ${rightShift}px)` : "100%";

  return (
    <>
      {isLoading && (
        <div className="flex h-full w-full">
          <InlineLoading className="justify-center" />
        </div>
      )}

      {!isLoading && (
        <div className="flex flex-row h-full w-full" ref={graphContainerRef}>
          <div
            ref={graphRef}
            style={{
              width,
              height: isFullScreen ? "calc(100% - 100px)" : "100%",
            }}
          />
          {selectedNode && rightShift && <div style={{ width: rightShift }} />}
        </div>
      )}
      <DrawerTransition withPortal={false}>
        {selectedNode && (
          <GraphNodePassiveModal
            key={selectedNode.id}
            node={selectedNode}
            onClose={resetSelectedNode}
          />
        )}
      </DrawerTransition>
    </>
  );
};

export default RelatedCasesGraphComponent;
